import React from "react";
import { Row, Col } from "react-bootstrap";
import { RBSModal } from "../../base/rbs_components/RBSModal.tsx";
import { RBSTextField } from "../../base/rbs_components/RBSTextField.tsx";
import { RBSDropdown } from "../../base/rbs_components/RBSDropdown.tsx";
import type {
    SyntheticTrancheInput,
    SyntheticTranche,
    WaterfallItemInput,
    WaterfallItem,
    TimeSeries,
    Variable
} from "../../base/ui_general/GeneralInterfaces.tsx";
import {
    DefaultDefinitionEnum, WaterfallItemTypeEnum, LabelTextMessageTypes, ValidationSchemes, TextfieldInputTypes,
    UserLanguages, DisplayFormatTypeEnum
} from "../../base/Settings.tsx";
import { getLabelTextMessage } from "../../base/utils/GeneralUtilities.tsx";
import type {InputStateControl} from "../../base/utils/InputStateControl.tsx";

interface SyntheticTrancheDetailModalProps {
    show: boolean;
    onClose: () => void;
    onSubmit: (syntheticTrancheInput: SyntheticTrancheInput, waterfallItemInput: WaterfallItemInput) => void;
    waterfall_item?: WaterfallItem;
    syntheticTranche?: SyntheticTranche;
    inputStateControl: InputStateControl;
    user_language: UserLanguages;
    isValid: boolean;
    /** Array of available time series for base_curve_id selection. */
    time_series: Array<TimeSeries> | undefined;
    /** Array of available variables for target_amortization_variable_id selection. */
    variables: Array<Variable> | undefined;
}

export const SyntheticTrancheDetailModal: React.FC<SyntheticTrancheDetailModalProps> = (props: SyntheticTrancheDetailModalProps) => {
    // Helper to prefill values from syntheticTranche or waterfall_item, or use defaults
    const prefill = (key: string): string => {
        if (props.syntheticTranche && key in props.syntheticTranche) {
            const value = props.syntheticTranche[key as keyof typeof props.syntheticTranche];
            if (key === "invested") return value ? "true" : "false";
            return String(value ?? "");
        }
        if (props.waterfall_item && key in props.waterfall_item) {
            const value = props.waterfall_item[key as keyof typeof props.waterfall_item];
            if (key === "invested") return value ? "true" : "false";
            return String(value ?? "");
        }
        switch (key) {
            case "base_curve_id":
                return "";
            case "name":
                return "New Synthetic Tranche";
            case "description":
                return "Synthetic tranche for transaction";
            case "initial_nominal":
                return "3000000";
            case "spread":
                return "0.015";
            case "default_definition":
                return String(DefaultDefinitionEnum.DEFERRABLE);
            case "invested":
                return "false"; // Default fallback for invested is string "false"
            case "target_return_on_equity":
                return "0.0";
            case  "target_amortization_variable_id":
                return "";
            default:
                return "";
        }
    };
    const headerTitle = props.syntheticTranche
        ? getLabelTextMessage(LabelTextMessageTypes.UPDATE_SYNTHETIC_TRANCHE_TITLE, props.user_language)
        : getLabelTextMessage(LabelTextMessageTypes.NEW_SYNTHETIC_TRANCHE_CREATION_TITLE, props.user_language);
    return (
        <RBSModal
            show={props.show}
            header_title={headerTitle}
            show_close_button
            onClose={props.onClose}
            primary_button_label={getLabelTextMessage(LabelTextMessageTypes.SUBMIT, props.user_language)}
            secondary_button_label={getLabelTextMessage(LabelTextMessageTypes.CANCEL, props.user_language)}
            onClickSecondary={props.onClose}
            primary_button_disabled={!props.isValid}
            size={"xl"}
            onClickPrimary={() => {
                const investedStr = props.inputStateControl.getStringValueForKey("invested");
                const baseCurveIdRaw = props.inputStateControl.getStringValueForKey("base_curve_id");
                const targetAmortizationVariableRaw = props.inputStateControl.getStringValueForKey(
                    "target_amortization_variable_id");
                const defaultDefinitionRaw = props.inputStateControl.getStringValueForKey("default_definition");
                let transactionIdRaw = props.inputStateControl.getStringValueForKey("transaction_id");
                if (!transactionIdRaw) transactionIdRaw = "";
                const syntheticTrancheInput: SyntheticTrancheInput = {
                    initial_nominal: Number(props.inputStateControl.getStringValueForKey("initial_nominal")),
                    spread: Number(props.inputStateControl.getStringValueForKey("spread")),
                    base_curve_id: baseCurveIdRaw === "" ? undefined : baseCurveIdRaw,
                    default_definition: Object.values(DefaultDefinitionEnum).includes(defaultDefinitionRaw as DefaultDefinitionEnum)
                        ? (defaultDefinitionRaw as DefaultDefinitionEnum)
                        : DefaultDefinitionEnum.DEFERRABLE,
                    invested: investedStr === "true",
                    target_return_on_equity: Number(props.inputStateControl.getStringValueForKey("target_return_on_equity")),
                    target_amortization_variable_id: targetAmortizationVariableRaw === "" ? undefined : targetAmortizationVariableRaw
                };
                const waterfallItemInput: WaterfallItemInput = {
                    name: props.inputStateControl.getStringValueForKey("name") ?? "",
                    description: props.inputStateControl.getStringValueForKey("description") ?? "",
                    transaction_id: transactionIdRaw,
                    type: WaterfallItemTypeEnum.SYNTHETIC_TRANCHE,
                };
                props.onSubmit(syntheticTrancheInput, waterfallItemInput);
                props.onClose();
            }}
        >
            <Row>
                <Col md={6}>
                    <RBSTextField
                        label={getLabelTextMessage(LabelTextMessageTypes.NAME, props.user_language)}
                        input_key="name"
                        prefill={prefill("name")}
                        validation_schemes={[ValidationSchemes.REQUIRED]}
                        input_state_control={props.inputStateControl}
                        type={TextfieldInputTypes.TEXT}
                    />
                </Col>
                <Col md={6}>
                    <RBSTextField
                        label={getLabelTextMessage(LabelTextMessageTypes.INITIAL_NOMINAL, props.user_language)}
                        input_key="initial_nominal"
                        prefill={prefill("initial_nominal")}
                        validation_schemes={[ValidationSchemes.REQUIRED, ValidationSchemes.NUMBER, ValidationSchemes.NON_NEGATIVE]}
                        input_state_control={props.inputStateControl}
                        type={TextfieldInputTypes.NUMBER}
                    />
                </Col>
            </Row>
            <Row>
                <Col md={4}>
                    <RBSTextField
                        label={getLabelTextMessage(LabelTextMessageTypes.SPREAD, props.user_language)}
                        input_key="spread"
                        prefill={prefill("spread")}
                        validation_schemes={[ValidationSchemes.REQUIRED, ValidationSchemes.NUMBER, ValidationSchemes.NON_NEGATIVE]}
                        input_state_control={props.inputStateControl}
                        type={TextfieldInputTypes.NUMBER}
                    />
                </Col>
                <Col md={4}>
                    <RBSDropdown
                        label={getLabelTextMessage(LabelTextMessageTypes.BASE_CURVE_ID, props.user_language)}
                        input_key="base_curve_id"
                        prefill={prefill("base_curve_id")}
                        items={[{ label: "---", value: "" }, ...(props.time_series ? props.time_series.map(ts => ({ label: ts.name, value: ts.id })) : [])]}
                        input_state_control={props.inputStateControl}
                        validation_schemes={[]}
                    />
                </Col>
                <Col md={4}>
                    <RBSDropdown
                        label={getLabelTextMessage(LabelTextMessageTypes.DEFAULT_DEFINITION, props.user_language)}
                        input_key="default_definition"
                        prefill={prefill("default_definition")}
                        items={Object.values(DefaultDefinitionEnum).map(dd => ({ label: dd, value: dd }))}
                        input_state_control={props.inputStateControl}
                        validation_schemes={[ValidationSchemes.REQUIRED]}
                    />
                </Col>
            </Row>
            <Row>
                <Col md={4}>
                    <RBSDropdown
                        label={getLabelTextMessage(LabelTextMessageTypes.INVESTED, props.user_language)}
                        input_key="invested"
                        prefill={prefill("invested")}
                        items={[{ label: "Yes", value: "true" }, { label: "No", value: "false" }]}
                        input_state_control={props.inputStateControl}
                        validation_schemes={[ValidationSchemes.REQUIRED]}
                    />
                </Col>
                <Col md={4}>
                    <RBSDropdown
                        label={getLabelTextMessage(LabelTextMessageTypes.TARGET_AMORTIZATION_VARIABLE_ID, props.user_language)}
                        input_key="target_amortization_variable_id"
                        prefill={prefill("target_amortization_variable_id")}
                        items={[{ label: "---", value: "" }, ...(props.variables ? props.variables.filter(
                            v => v.display_format === DisplayFormatTypeEnum.AMOUNT).sort(
                                (a, b) => a.name.localeCompare(b.name)).map(
                                v => ({ label: v.name, value: v.id })) : [])]}
                        input_state_control={props.inputStateControl}
                        validation_schemes={[]}
                    />
                </Col>
                <Col md={4}>
                    <RBSTextField
                        label={getLabelTextMessage(LabelTextMessageTypes.TARGET_RETURN_ON_EQUITY, props.user_language)}
                        input_key="target_return_on_equity"
                        prefill={prefill("target_return_on_equity")}
                        validation_schemes={[ValidationSchemes.REQUIRED, ValidationSchemes.NUMBER, ValidationSchemes.NON_NEGATIVE]}
                        input_state_control={props.inputStateControl}
                        type={TextfieldInputTypes.NUMBER}
                    />
                </Col>
            </Row>
            <Row>
                <Col md={12}>
                    <RBSTextField
                        label={getLabelTextMessage(LabelTextMessageTypes.DESCRIPTION, props.user_language)}
                        input_key="description"
                        prefill={prefill("description")}
                        validation_schemes={[ValidationSchemes.REQUIRED]}
                        input_state_control={props.inputStateControl}
                        type={TextfieldInputTypes.TEXT}
                        multiline={true}
                        style={{ minHeight: 300 }}
                    />
                </Col>
            </Row>
        </RBSModal>
    );
}
